// Written on the elements that sit against the main panel, never on the root: a custom property
// set on `<html>` restyles every element on the page, once per frame while a side panel slides.
const areas = new Set<HTMLElement>();
let edges: { left: string; right: string } | null = null;

function paint(area: HTMLElement) {
  if (edges) {
    area.style.setProperty("--toast-left", edges.left);
    area.style.setProperty("--toast-right", edges.right);
  } else {
    area.style.removeProperty("--toast-left");
    area.style.removeProperty("--toast-right");
  }
}

/** A ref for the toaster or a card placed as a toast is: it takes the main panel's edges. */
export function toastArea(area: HTMLElement) {
  areas.add(area);
  paint(area);
  return () => {
    areas.delete(area);
  };
}

let toaster: HTMLElement | null = null;

/** The toaster's ref: a toast area that a card at the bottom edge lifts its toasts above. */
export function toasterArea(area: HTMLElement) {
  toaster = area;
  const forget = toastArea(area);
  return () => {
    forget();
    toaster = null;
  };
}

/**
 * A ref for a card held at the bottom edge until it is answered: while it shows, toasts sit above
 * it, never over it. Measured from its own `bottom` and height, so its entry transform and a
 * moving mobile toolbar do not count.
 */
export function toastCardArea(card: HTMLElement) {
  const forget = toastArea(card);
  const lift = () =>
    toaster?.style.setProperty(
      "--toast-above",
      `${Number.parseFloat(getComputedStyle(card).bottom) + card.offsetHeight}px`,
    );
  const observer = new ResizeObserver(lift);
  observer.observe(card);
  window.addEventListener("resize", lift);
  return () => {
    forget();
    observer.disconnect();
    window.removeEventListener("resize", lift);
    toaster?.style.removeProperty("--toast-above");
  };
}

/**
 * The main panel's ref: its edges go to every `toastArea` as `--toast-left` and `--toast-right`, so
 * a toast centres on the panel and not on the window a side panel shares.
 */
export function holdToastsOver(main: HTMLElement | null) {
  if (!main) return;
  const observer = new ResizeObserver(() => {
    const { left, right } = main.getBoundingClientRect();
    edges = { left: `${left}px`, right: `${right}px` };
    for (const area of areas) paint(area);
  });
  observer.observe(main);
  return () => {
    observer.disconnect();
    edges = null;
    for (const area of areas) paint(area);
  };
}
